export type NestDialMotion = { angle: number, velocity: number }

// A critically damped response: the ring takes about a second to approach a new
// target, and successive inputs preserve its momentum without springy overshoot.
const RESPONSE = 5.2
export const NEST_DIAL_ANGLES = Array.from({ length: 151 }, (_, index) => -150 + index * 2)

export const NEST_ACTIVITY_CYCLE_MS = 2800
const ACTIVITY_TRAVEL_MS = 1600

/** Stagger a short pulse from the measured temperature toward the active equipment target. */
export function getNestActivityTicks(ambient: number, target?: number) {
  if (target === undefined || !Number.isFinite(ambient) || !Number.isFinite(target)
    || Math.abs(target - ambient) < 0.001) {
    return []
  }
  const first = Math.min(ambient, target) + 0.001
  const last = Math.max(ambient, target) - 0.001
  return NEST_DIAL_ANGLES.flatMap((angle) => {
    const progress = (angle - ambient) / (target - ambient)
    return angle > first && angle < last
      ? [{ angle, delayMilliseconds: progress * ACTIVITY_TRAVEL_MS - NEST_ACTIVITY_CYCLE_MS }]
      : []
  })
}

export function advanceNestDial(motion: NestDialMotion, target: number, seconds: number): NestDialMotion {
  const decay = Math.exp(-RESPONSE * seconds)
  const offset = motion.angle - target
  const impulse = (motion.velocity + RESPONSE * offset) * seconds
  return {
    angle: target + (offset + impulse) * decay,
    velocity: (motion.velocity - RESPONSE * impulse) * decay,
  }
}

/** Each bar briefly lengthens as the marker passes, then recedes into its wake. */
export function advanceNestDialWake(
  length: number, tick: number, previous: number, motion: NestDialMotion, seconds: number,
) {
  const distance = tick < Math.min(previous, motion.angle)
    ? Math.min(previous, motion.angle) - tick
    : tick > Math.max(previous, motion.angle) ? tick - Math.max(previous, motion.angle) : 0
  const strength = Math.min(1, Math.abs(motion.velocity) / 18)
  const pulse = 8.5 * strength * Math.exp(-((distance / 3.8) ** 2))
  const next = Math.max(length * Math.exp(-seconds / 0.3), pulse)
  return next < 0.025 ? 0 : next
}

export function nestDialIsSettled(motion: NestDialMotion, target: number, wake: readonly number[]) {
  return Math.abs(motion.angle - target) < 0.015 && Math.abs(motion.velocity) < 0.035
    && wake.every((length) => length === 0)
}
